import { resolveComboLinePrices } from '@tcr-erp/shared';
import { recalcItem, variantToOrderItem } from './orderItemCalc';
import { mapDetailToPosVariant } from './mapVariantDetail';
import type {
  FulfillmentMode,
  OrderItem,
  PosComboSearchItem,
  PosVariantDetailItem,
} from '../types';

export interface ComboStockShortage {
  product_name: string;
  /** Tổng số lượng cần (qty thành phần × SL combo; SL combo mặc định 1). */
  needed: number;
  /** Tồn khả dụng tại chi nhánh, tra tươi lúc bấm thêm. */
  available: number;
}

/** Chi tiết variant theo `variant_code` — combo lưu `variant_code` ở `variant_code`. */
function indexByCode(details: PosVariantDetailItem[]): Map<string, PosVariantDetailItem> {
  return new Map(details.map((detail) => [detail.variant.variant_code, detail]));
}

/**
 * Thành phần nào không đủ tồn cho MỘT combo.
 *
 * PREORDER trả rỗng: đặt trước là bán hàng chưa có, và `lookupVariants` ở chế độ
 * đó cũng cố ý không chặn theo tồn.
 *
 * `comboQty` (mặc định 1): số combo định bán — nhu cầu mỗi thành phần = qty × comboQty.
 * Popup "Thêm nhanh" cho tăng SL combo sau khi tick nên phải kiểm lại theo SL này
 * (TCR-2407); `needed` trả về là TỔNG nhu cầu đã nhân.
 */
export function findComboShortages(
  combo: PosComboSearchItem,
  details: PosVariantDetailItem[],
  fulfillmentMode: FulfillmentMode,
  comboQty = 1,
): ComboStockShortage[] {
  if (fulfillmentMode !== 'INSTANT') return [];

  const byCode = indexByCode(details);
  const shortages: ComboStockShortage[] = [];
  for (const component of combo.component_items) {
    const detail = byCode.get(component.variant_code);
    const available = detail?.stock_quantity ?? 0;
    const needed = component.qty * comboQty;
    if (available < needed) {
      shortages.push({
        product_name: component.product_name,
        needed,
        available,
      });
    }
  }
  return shortages;
}

/**
 * Thêm MỘT combo vào giỏ dưới dạng một cụm mới.
 *
 * Mỗi thành phần là một dòng riêng mang `comboGroupId` chung — combo KHÔNG phải
 * một dòng. Giá dòng lấy từ `resolveComboLinePrices` — đọc thẳng `price_in_combo`
 * đã chốt khi component đủ backfill, rơi về phân bổ theo `combo_price` (cùng hàm
 * BE dùng để chốt số) khi chưa — nên số hiển thị luôn khớp số thu.
 *
 * Luôn tạo cụm MỚI, kể cả khi giỏ đã có cụm của cùng combo: `setComboGroupCount`
 * cố ý ném lỗi khi giỏ không đủ hàng lẻ ("không tự mua hộ khách"), còn cộng vào
 * dòng combo sẵn có sẽ phá bất biến `quantity == comboUnitQty × comboUnitCount`.
 */
export function addComboToCart(
  items: OrderItem[],
  combo: PosComboSearchItem,
  details: PosVariantDetailItem[],
  fulfillmentMode: FulfillmentMode,
  newId: () => string,
): OrderItem[] {
  const byCode = indexByCode(details);
  const groupId = newId();
  const pricing = resolveComboLinePrices(
    combo.component_items.map((component) => ({
      variant_id: component.variant_id,
      qty: component.qty,
      retail_price: component.retail_price,
      price_in_combo: component.price_in_combo,
    })),
    combo.combo_price,
    1,
  );

  const lines: OrderItem[] = combo.component_items.map((component, index) => {
    const detail = byCode.get(component.variant_code);
    if (!detail) {
      throw new Error(`Không lấy được thông tin sản phẩm: ${component.product_name}`);
    }
    // Tra theo INDEX, không theo `variant_id`: `resolveComboLinePrices` sinh
    // `pricing.lines` bằng `.map(...)` trên chính `combo.component_items`
    // (dòng dựng `pricing` ở trên), nên hai mảng LUÔN cùng thứ tự, cùng độ
    // dài — tra theo index là 1:1 an toàn tuyệt đối. Tra theo `variant_id` sai
    // khi một combo khai trùng cùng `variant_id` ở hai dòng thành phần
    // (`combo.service.ts` không chặn việc này): `.find()` luôn khớp dòng ĐẦU
    // TIÊN nên cả hai dòng thành phần nhận cùng một `unit_price`, làm tổng
    // dòng lệch.
    const priced = pricing.lines[index];
    if (!priced) {
      throw new Error(`Không tính được giá combo cho: ${component.product_name}`);
    }

    // Dựng qua đúng đường của luồng thêm SP thường để có đủ vat, kho, brand,
    // snapshot giá vàng — tự dựng OrderItem từ component_items sẽ thiếu âm thầm.
    const base = variantToOrderItem(mapDetailToPosVariant(detail), fulfillmentMode, newId);

    return recalcItem(
      {
        ...base,
        quantity: component.qty,
        price: priced.unit_price,
        discountPercent: 0,
        // Giá lẻ TƯƠI từ lookup. `component.retail_price` là ảnh chụp lúc cấu
        // hình combo, không bao giờ đồng bộ lại — dùng nó làm giá gạch ngang là
        // quảng cáo một mức tiết kiệm tính trên giá đã cũ.
        retailPrice: base.price,
        comboGroupId: groupId,
        comboId: combo.combo_id,
        comboSku: combo.combo_sku,
        comboName: combo.combo_name,
        comboUnitQty: component.qty,
        comboUnitCount: 1,
        comboUnitPrice: pricing.effective_unit_price,
        comboRetailTotal: pricing.retail_unit_total,
        // Cơ số giá lẻ mà `resolveComboLinePrices` vừa dùng để phân bổ `price` ở
        // trên — giữ nguyên bên cạnh `retailPrice` (giá tươi) để cột "Giá Tiền"
        // của dòng thành phần cộng lại đúng bằng `comboRetailTotal` của cụm.
        comboComponentRetailPrice: component.retail_price,
        splitComboGroupId: undefined,
      },
      fulfillmentMode,
    );
  });

  return [...items, ...lines];
}
